import { CommonModule, CurrencyPipe } from '@angular/common'; // TODO: remove/move CurrencyPipe to SharedModule [future release]
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  NgModule,
  LOCALE_ID,
  ErrorHandler,
  DEFAULT_CURRENCY_CODE
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  AgpSharedAddressModule,
  AgpSharedAggregatorComponentRegistryModule
} from '@liberty-financial/agp-shared';

import { ApiHttp } from '@core/api/api-http';
import { Claims } from '@core/authentication/claims';
import { MockApiInterceptor } from '@core/interceptors/mock-api.interceptor';
import { OpportunityTypeInterceptor } from '@core/interceptors/opportunity-type.interceptor';
import { TraceHttpInterceptor } from '@core/interceptors/TraceHttpInterceptor';
import { PageTitleStrategy } from '@core/services/page-title.strategy';
import { ComingSoonComponent } from '@core/pages/coming-soon/coming-soon.component';
import { ErrorPageComponent } from '@core/pages/error/error-page.component';
import { LoginFailedComponent } from '@core/pages/login-failed/login-failed.component';
import { NotFoundComponent } from '@core/pages/not-found/not-found.component';
import { RedirectComponent } from '@core/pages/redirect/redirect.component';
import { UnauthorisedComponent } from '@core/pages/unauthorised/unauthorised.component';
import { ErrorHandlerService } from '@core/services/error-handler.service';
import { aggregator } from '@env/aggregator/aggregator';
import { AuthenticatedModule } from '@features/authenticated/authenticated.module';
import { ContactsModule } from '@features/contacts/contacts.module';
import { DashboardModule } from '@features/dashboard/dashboard.module';
import { DiaryNotesModule } from '@features/diary-notes/diary-notes.module';
import { EventsModule } from '@features/events/events.module';
import { SettingsModule } from '@features/settings/settings.module';
import { TasksModule } from '@features/tasks/tasks.module';
import { LoadModule } from '@shared/components/load/load.module';
import { MenuModule } from '@shared/components/menu/menu.module';
import { SearchModule } from '@shared/components/search/search.module';
import { SparkShellModule } from '@shared/components/shell/shell.module';
import { VehicleDetailsModule } from '@shared/components/vehicle-details/vehicle-details.module';
import { PipesModule } from '@shared/pipes/pipes.module';
import { SharedModule } from '@shared/shared.module';
import { GoogleMapsService } from '@shared/services/google-maps.service';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { loginRequest, msalConfig, protectedResources } from './auth-config';
import { environment } from '../environments/environment';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(
    protectedResources.opportunityApi.endpoint,
    protectedResources.opportunityApi.scopes
  );
  protectedResourceMap.set(
    protectedResources.clientsApi.endpoint,
    protectedResources.clientsApi.scopes
  );
  protectedResourceMap.set(
    protectedResources.managementApi.endpoint,
    protectedResources.managementApi.scopes
  );
  protectedResourceMap.set(
    protectedResources.documentApi.endpoint,
    protectedResources.documentApi.scopes
  );
  protectedResourceMap.set(
    protectedResources.iqAuthApi.endpoint,
    protectedResources.iqAuthApi.scopes
  );
  protectedResourceMap.set(
    protectedResources.libraryApi.endpoint,
    protectedResources.libraryApi.scopes
  );
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: {
      state: window.location.href,
      extraQueryParameters: {
        aggregator: aggregator.name
      }
    }
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
    loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ComingSoonComponent,
    ErrorPageComponent,
    NotFoundComponent,
    UnauthorisedComponent,
    RedirectComponent,
    LoginFailedComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    ContactsModule,
    HttpClientModule,
    PipesModule,
    SearchModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MenuModule,
    LoadModule,
    SharedModule,
    DashboardModule,
    TasksModule,
    EventsModule,
    DiaryNotesModule,
    SettingsModule,
    MsalModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.useServiceWorkers,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    SparkShellModule,
    AuthenticatedModule,
    AgpSharedAddressModule.forRoot({
      googlePlace: {
        countries:
          aggregator.settings.claims.defaults.country ==
          Claims.Country.NewZealand
            ? ['nz']
            : ['au', 'nz']
      },
      manualEntry: {
        countries: ['au', 'nz'],
        defaultCountry:
          aggregator.settings.claims.defaults.country ==
          Claims.Country.NewZealand
            ? 'New Zealand'
            : 'Australia'
      },
      requirements: {
        Australia: {
          streetNumber: {
            required: true
          }
        },
        'New Zealand': {
          streetNumber: {
            required: true
          }
        }
      }
    }),
    AgpSharedAggregatorComponentRegistryModule.forRoot(aggregator),
    VehicleDetailsModule.forRoot({
      glassGuideApiBaseUrl: environment.glassGuideApi.url,
      glassGuideApiKey: environment.glassGuideApi.key
    })
  ],
  providers: [
    ApiHttp,
    {
      provide: LOCALE_ID,
      useValue: Claims.Country.toLocale(
        aggregator.settings.claims.defaults.country
      )
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: Claims.Country.toCurrencyCode(
        aggregator.settings.claims.defaults.country
      )
    },
    CurrencyPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MockApiInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TraceHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OpportunityTypeInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy
    }
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
  constructor(private googleMapsService: GoogleMapsService) {
    // Initialize Google Maps API
    this.googleMapsService.loadGoogleMapsApi().subscribe();
  }
}
